import { Communication, Plugins } from "neo-games";
import { WebSocketClient } from "../Utils/WebSocketClient";
import { BalanceManager } from "./BalanceManager";
import { GameManager } from "./GameManager";
import { ApiClientCommunication } from "neo-common";
import { ModuleManager } from "./ModuleManager";
import { PageVisibilityHandler } from "../CustomComponents/PageVisibilityHandler";
import { HtmlController } from "../HTMLComponents/HtmlController";
import { AppContext } from "../../Common/AppContext";
import { FreeRoundBonus } from "../Channels/Common/Bonuses/FreeRoundBonus";
import { Scaling } from "../Utils/Scaling";
import { WebSocketClientHub } from "../Utils/WebSocketClientHub";
import { DataManager } from "./DataManager";
import { Jackpot } from "../Channels/Common/Bonuses/Jackpot";

export class NotificationManager {
    //******************************************************************************************************************
    //  INSTANCE
    //******************************************************************************************************************

    private static instance: NotificationManager;

    //******************************************************************************************************************
    //  VARIABLES
    //******************************************************************************************************************

    private webSocketClient: WebSocketClient;

    private webSocketClientHub: WebSocketClientHub;

    //******************************************************************************************************************
    //  CONSTRUCTOR
    //******************************************************************************************************************

    constructor() {
        if (AppContext.gameConfiguration.ApplicationGatewayUrl) {
            this.webSocketClientHub = new WebSocketClientHub();
        } else {
            this.webSocketClient = new WebSocketClient();
        }

        this.bindMessages();
    }

    //******************************************************************************************************************
    //  PRIVATE FUNCTIONS
    //******************************************************************************************************************

    private bindMessages(): void {
        if (this.webSocketClient != null) {
            this.webSocketClient.on("RefreshBalance", this.refreshBalance.bind(this));
            this.webSocketClient.on("CloseSession", this.closeSession.bind(this));
            this.webSocketClient.on("UpdateStatusBar", this.updateStatusBar.bind(this));
        }

        if (this.webSocketClientHub != null) {
            this.onReceiveMessage(this.messageReceiver.bind(this));
        }
    }

    //******************************************************************************************************************
    //  GAME 2 SERVER
    //******************************************************************************************************************

    private sendMessage(message: Message): void {
        if (this.webSocketClient != null) {
            let methodName = message.action;
            let data = message.data;

            if (methodName == "event/log") {
                methodName = "LogEvent";
            }

            this.webSocketClient.sendMessage(methodName, data);
        }

        if (this.webSocketClientHub != null) {
            if (!this.webSocketClientHub.isConnectionEstablished()) {
                this.webSocketClientHub.addMessage(this.sendMessage.bind(this, message));
                return;
            }

            this.webSocketClientHub
                .sendMessage(message)
                .then(() => {
                    if (DEBUG) {
                        console.log("Message received");
                    }
                })
                .catch((error) => {
                    if (DEBUG) {
                        console.log(error);
                    }
                });
        }
    }

    private invokeCommand(message: Message): Promise<Result> {
        if (this.webSocketClientHub != null) {
            return this.webSocketClientHub.invokeCommand(message);
        }

        return Promise.reject({ code: "Hub connection not initialized" } as Result);
    }

    private onReceiveMessage(receiver: (message: Message) => void): void {
        if (this.webSocketClientHub != null) {
            this.webSocketClientHub.onReceiveMessage(receiver);
        }
    }

    private disconnect(): void {
        if (this.webSocketClientHub != null) {
            this.webSocketClientHub.disconnect();
        }
    }

    //******************************************************************************************************************
    //  SERVER 2 GAME
    //******************************************************************************************************************

    private messageReceiver(message: Message): void {
        if (message == null) {
            if (DEBUG) {
                console.log("ERROR - message object was not received from websocket");
            }
            return;
        }

        if (message.application.toLowerCase() != "neo") {
            if (DEBUG) {
                console.log('ERROR - message application was not "NEO"');
            }
            return;
        }

        if (message.service.toLowerCase() == "notification") {
            switch (message.action.toLowerCase()) {
                case "refreshbalance":
                    this.refreshBalance(message.data);
                    break;
                case "closesession":
                    this.closeSession();
                    break;
                case "updatestatusbar":
                    this.updateStatusBar(message.data);
                    break;
            }
        }

        if (message.service.toLowerCase() == "servertime") {
            switch (message.action.toLowerCase()) {
                case "update":
                    this.updateServerTime(message.data);
                    break;
            }
        }
    }

    private refreshBalance(data: any): void {
        //dynamic frb
        if (data["freeRounds"] != null) {
            FreeRoundBonus.updateRemainingCount(data["freeRounds"]);
        }

        //we dont want to update balance during mini game
        if (Jackpot.hasActiveGame()) {
            return;
        }

        BalanceManager.balanceReceived(data as Communication.Game.IBalance);
    }

    private closeSession(): void {
        for (let key in Communication.Game.GameModules) {
            ModuleManager.disableChannelModule(Communication.Game.GameModules[key]);
        }

        PageVisibilityHandler.Enabled = false;
        PageVisibilityHandler.pauseAll();
        HtmlController.dispatchActionAllowed = false;

        GameManager.errorReceived({
            requestInfo: {
                request: NotificationManager,
                url: null,
                gameKey: null,
            } as ApiClientCommunication.Types.IRequestInfo,
            httpStatusCode: 401,
        } as ApiClientCommunication.Types.ApiClientError);
    }

    private updateStatusBar(data: any): void {
        let statusBar = Plugins.StatusBar.getInstance();
        if (statusBar.hasActiveWidget("statusBarMessage") && data != null && data["statusBarMessage"]) {
            statusBar.add(Plugins.StatusBar.Types.StatusBarMessage, "statusBarMessage", data["statusBarMessage"]);
            Scaling.resizeRoot();
        }
    }

    private updateServerTime(data: any): void {
        if (data == null) {
            return;
        }

        DataManager.serverTimeReceived(data);
    }

    //******************************************************************************************************************
    //  PUBLIC FUNCTIONS
    //******************************************************************************************************************

    public static initialize(): void {
        if (NotificationManager.instance == null) {
            NotificationManager.instance = new NotificationManager();
        }
    }

    public static sendMessage(message: Message): void {
        if (NotificationManager.instance != null) {
            NotificationManager.instance.sendMessage(message);
        }
    }

    public static invokeCommand(message: Message): Promise<Result> {
        if (NotificationManager.instance != null) {
            return NotificationManager.instance.invokeCommand(message);
        }

        return Promise.reject({ code: "Hub connection not initialized" } as Result);
    }

    public static onReceiveMessage(receiver: (message: Message) => void): void {
        if (NotificationManager.instance != null) {
            return NotificationManager.instance.onReceiveMessage(receiver);
        }
    }

    public static disconnect(): void {
        if (NotificationManager.instance != null) {
            NotificationManager.instance.disconnect();
        }
    }
}

export interface Message {
    application: string;
    service: string;
    action: string;
    data: Object;
}

export interface Result {
    code: string;
    data: Object;
}

export interface ResultError extends Error {
    result: Result;
}



/** WEBPACK FOOTER **
 ** ./Engine/Game/Managers/NotificationManager.ts
 **/